// src/components/OverviewContent.tsx

import React, { useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OverviewObject } from '../types/DataObject';
import { StreamingText } from './StreamingText';
import { AnimationConfig } from '../config/animationConfig';

interface OverviewContentProps {
  overview: OverviewObject | null;
  inProgress?: boolean;
  selectedOverviewIndex?: number;
}

export const OverviewContent: React.FC<OverviewContentProps> = ({
  overview,
  inProgress = false,
  selectedOverviewIndex = 0
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(-1);
  const [activeResolvedIndex, setActiveResolvedIndex] = useState(-1);
  const [showCurrentResponse, setShowCurrentResponse] = useState(false);

  const resetAnimation = useCallback(() => {
    setActiveQuestionIndex(-1);
    setActiveResolvedIndex(-1);
    setShowCurrentResponse(false);
  }, []);

  useEffect(() => {
    resetAnimation();
    if (overview) {
      if (inProgress) {
        setActiveQuestionIndex(0);
      } else {
        setActiveQuestionIndex((overview.questions?.length ?? 0) - 1);
        setActiveResolvedIndex((overview.resolved?.length ?? 0) - 1);
        setShowCurrentResponse(true);
      }
    }
  }, [overview, inProgress, resetAnimation]);

  const handleQuestionComplete = useCallback(() => {
    if (!overview || !inProgress || !overview.questions) return;

    requestAnimationFrame(() => {
      setActiveQuestionIndex(prev => {
        if (prev + 1 < overview.questions.length) {
          return prev + 1;
        } else {
          setTimeout(
            () => setActiveResolvedIndex(0),
            AnimationConfig.sections.delayBetweenSections
          );
          return prev;
        }
      });
    });
  }, [overview, inProgress]);

  const handleResolvedComplete = useCallback(() => {
    if (!overview || !inProgress || !overview.resolved) return;

    setTimeout(() => {
      setActiveResolvedIndex(prev => {
        if (prev + 1 < overview.resolved.length) {
          return prev + 1;
        } else {
          setTimeout(
            () => setShowCurrentResponse(true),
            AnimationConfig.sections.delayBetweenSections
          );
          return prev;
        }
      });
    }, AnimationConfig.sections.delayBetweenSections);
  }, [overview, inProgress]);

  // If no overview data, return
  if (!overview) {
    console.warn('Overview data not found.');
    return <div />;
  }

  // Ensure arrays exist
  const questions = overview.questions || [];
  const resolved = overview.resolved || [];
  const currentResponse = overview.current_response || '';

  return (
    <>
      <div className="questions-section">
        {questions.map((question, index) => {
          const isActive = index === activeQuestionIndex;
          const isComplete = index < activeQuestionIndex || !inProgress;

          return (
            <motion.div
              key={`question-${index}-${selectedOverviewIndex}`}
              className="question-container"
              initial={{ opacity: 0, x: -20, scale: 0.95 }}
              animate={{
                opacity: isActive || isComplete ? 1 : 0,
                x: isActive || isComplete ? 0 : -20,
                scale: isActive || isComplete ? 1 : 0.95
              }}
              transition={{
                duration: 0.3,
                ease: [0.34, 1.56, 0.64, 1],
              }}
            >
              <span className="question-number">{index + 1}.</span>
              {isActive ? (
                <StreamingText
                  key={`streaming-${index}-${selectedOverviewIndex}`}
                  text={question}
                  className="question-text"
                  onComplete={handleQuestionComplete}
                />
              ) : isComplete ? (
                <div
                  className="question-text"
                  dangerouslySetInnerHTML={{ __html: question }}
                />
              ) : null}
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {((activeQuestionIndex === questions.length - 1 && questions.length > 0) || !inProgress) && (
          <motion.div
            key={`resolved-section-${selectedOverviewIndex}`}
            className="resolved-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            {resolved.map((resolvedItem, index) => (
              <motion.div
                key={`resolved-${index}-${selectedOverviewIndex}`}
                className="resolved-item"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{
                  opacity: index <= activeResolvedIndex || !inProgress ? 1 : 0,
                  scale: index <= activeResolvedIndex || !inProgress ? 1 : 0.9
                }}
                transition={{
                  duration: 0.3,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                onAnimationComplete={() => {
                  if (index === activeResolvedIndex && inProgress) {
                    handleResolvedComplete();
                  }
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: resolvedItem }} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {((activeResolvedIndex === resolved.length - 1 && resolved.length > 0) || !inProgress) &&
          showCurrentResponse && (
            <motion.div
              key={`current-response-${selectedOverviewIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              {inProgress ? (
                <StreamingText
                  key={`streaming-response-${selectedOverviewIndex}`}
                  text={currentResponse}
                  className="current-response"
                  startDelay={200}
                />
              ) : (
                <div
                  className="current-response"
                  dangerouslySetInnerHTML={{ __html: currentResponse }}
                />
              )}
            </motion.div>
          )}
      </AnimatePresence>
    </>
  );
};